<template>
  <div>
    <!-- Photo Limit Dialog -->
    <v-dialog
      v-model="photoLimitDialogVisible"
      max-width="600"
    >
      <div class="need_login_dialog style_dialog">
        <h2 class="header">
          Oops!
        </h2>
        <div class="cnt">
          Image does not fit criteria, please choose a JPG or PNG photo at least 1,000 pixels in width and length as well as less than 10 mb
          in size.
        </div>
        <div class="wrap_actions">
          <button type="button" class="ok" @click="selectNewPhoto" v-ripple>Ok</button>
          <button type="button" class="cancel" @click="closePhotoLimitDialog" v-ripple>Cancel</button>
        </div>
      </div>
    </v-dialog>

    <!-- Track Limit Dialog -->
    <v-dialog
      v-model="trackLimitDialogVisible"
      max-width="600"
    >
      <div class="need_login_dialog style_dialog">
        <h2 class="header">
          Oops!
        </h2>
        <div class="cnt">
          Please select a file smaller than 200MB.
        </div>
        <div class="wrap_actions center">
          <button type="button" class="tetriatary_btn" @click="closeTrackLimitDialog" v-ripple>Ok</button>
        </div>
      </div>
    </v-dialog>

    <!-- Upload Limit Dialog -->
    <v-dialog
      v-model="uploadLimitDialogVisible"
      :content-class="'style4'"
      max-width="600"
    >
      <h2 class="header_dialog">
        Upload Limit
      </h2>
      <div class="cnt_dialog center">
        50 files per release
      </div>
      <div class="wrap_actions_dialog center">
        <button type="button" class="tetriatary_btn" @click="closeUploadLimitDialog" v-ripple>Ok</button>
        <a :href="discordLink" target="_blank" class="primary_btn" v-ripple>Contact Support</a>
      </div>
    </v-dialog>

    <!-- Warning Remove Track Dialog -->
    <v-dialog
      v-model="warningRemoveTrackDialogVisible"
      max-width="570"
      :content-class="'warning_remove_track_dialog'"
    >
      <div class="heading">
        <h2>Warning</h2>
        <button class="close_modal" @click="closeWarningRemoveTrackDialog"></button>
      </div>
      <div class="description_modal">
        Removing this track will affect the users who purchased and or added this track to their favorites.
      </div>
      <div class="wrapper_action_modal">
        <button class="tetriatary_btn" @click="closeWarningRemoveTrackDialog">Cancel</button>
        <button 
          class="red_btn" 
          v-ripple 
          @click="confirmRemoveTrack" 
          :class="{'delete-process': isTrackBeingDeleted}"
        >
          <v-progress-circular v-if="isTrackBeingDeleted" :width="1" indeterminate></v-progress-circular>
          <span v-else>Continue</span>
        </button>
      </div>
    </v-dialog>

    <!-- Warning Change Artist Dialog -->
    <v-dialog
      v-model="warningChangeArtistDialogVisible"
      max-width="570"
      :content-class="'warning_remove_track_dialog'"
    >
      <div class="heading">
        <h2>Warning</h2>
        <button class="close_modal" @click="closeWarningChangeArtistDialog"></button>
      </div>
      <div class="description_modal">
        Changing the Artist on this release will affect the users who purchased and or added the release to their favorites.
      </div>
      <div class="wrapper_action_modal">
        <button class="tetriatary_btn" @click="closeWarningChangeArtistDialog">Cancel</button>
        <button class="red_btn" v-ripple @click="confirmChangeArtist">Continue</button>
      </div>
    </v-dialog>

    <!-- Lossless Upload Failed Dialog -->
    <v-dialog 
      v-model="losslessUploadedFailedDialogVisible"
      max-width="600"
    >
      <div class="need_login_dialog style_dialog">
        <h2 class="header">
          Oops!
        </h2>
        <div class="cnt">
          Some tracks failed to upload.
        </div>
        <div class="wrap_actions center">
          <button type="button" class="tetriatary_btn" @click="closeLosslessUploadedFailedDialog" v-ripple>Ok</button>
        </div>
      </div>
    </v-dialog>

    <!-- Offline Connection Dialog -->
    <v-dialog 
      v-model="isOfflineVisible" 
      :content-class="'style4 interrupted'" 
      max-width="600"
    >
      <div class="need_login_dialog style_dialog">
        <h2 class="header">
          Oops!
        </h2>
        <div class="cnt">
          Connection interrupted. Reconnecting in {{ secondsRemaining }} seconds...
        </div>
        <div class="wrap_actions_dialog center">
          <button type="button" class="next primary_btn" v-ripple @click="retryConnection">
            <span>Retry</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Artist Already Secondary Dialog -->
    <v-dialog
      v-model="artistAlreadySecondaryDialogVisible"
      persistent
      :content-class="'create_new_account_modal creator_account_renewal_dialog'"
      max-width="550"
    >
      <div>
        <div class="heading">
          <h2 class="heading_new_account">Artist Selection</h2>
        </div>
        <div class="wrapper_content h165" style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div class="account_issue_box">
            <div class="warning-message">
              <a :href="getArtistProfileUrl()" target="_blank" class="account-name-link">{{ artistToMove }}</a> has already been selected as a Secondary Artist. Do you want to move this artist to Primary Artist?
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="closeArtistAlreadySecondaryDialog">Cancel</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="confirmMoveArtistToPrimary">Move to Primary</button>
        </div>
      </div>
    </v-dialog>

    <!-- Clear Label Dialog - shown when switching from Record Label to Artist Account flow -->
    <v-dialog
      v-model="clearLabelDialogVisible"
      persistent
      :content-class="'create_new_account_modal creator_account_renewal_dialog'"
      max-width="550"
    >
      <div>
        <div class="heading">
          <h2 class="heading_new_account">Change Flow</h2>
        </div>
        <div class="wrapper_content h165" style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div class="account_issue_box">
            <div class="warning-message" style="text-align:center;">
              This will clear the currently selected record label
              <div style="margin: 12px 0; font-weight: bold;">
                <span class="account-name-link">{{ labelToRemove }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="clearLabelDialogVisible = false; $emit('cancel-clear-label');">Cancel</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="clearLabelDialogVisible = false; $emit('confirm-clear-label');">Continue</button>
        </div>
      </div>
    </v-dialog>

    <!-- Clear Secondary Artists Dialog - shown when switching from Multi-Artist to Single Artist flow -->
    <v-dialog
      v-model="clearSecondaryArtistsDialogVisible"
      persistent
      :content-class="'create_new_account_modal creator_account_renewal_dialog'"
      max-width="550"
    >
      <div>
        <div class="heading">
          <h2 class="heading_new_account">Change Structure</h2>
        </div>
        <div class="wrapper_content h165" style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div class="account_issue_box">
            <div class="warning-message" style="text-align:center;">
              This will clear all currently selected Secondary Artists
              <div style="margin: 12px 0; font-weight: bold;">
                <span class="account-name-link">{{ secondaryArtistsCount }} artist{{ secondaryArtistsCount !== 1 ? 's' : '' }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="clearSecondaryArtistsDialogVisible = false; $emit('cancel-clear-secondary-artists');">Cancel</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="clearSecondaryArtistsDialogVisible = false; $emit('confirm-clear-secondary-artists');">Continue</button>
        </div>
      </div>
    </v-dialog>

    <!-- Clear Artists Dialog - shown when switching to Various Artists Release structure -->
    <v-dialog
      v-model="clearArtistsDialogVisible"
      persistent
      :content-class="'create_new_account_modal creator_account_renewal_dialog'"
      max-width="550"
    >
      <div>
        <div class="heading">
          <h2 class="heading_new_account">Change Structure</h2>
        </div>
        <div class="wrapper_content h165" style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
          <div class="account_issue_box">
            <div class="warning-message" style="text-align:center;">
              This will clear all currently selected artists
              <div style="margin: 12px 0;">
                <span v-html="artistsToClear"></span>
              </div>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="clearArtistsDialogVisible = false; $emit('cancel-clear-artists');">Cancel</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="clearArtistsDialogVisible = false; $emit('confirm-clear-artists');">Continue</button>
        </div>
      </div>
    </v-dialog>

    <!-- Structure Selection Dialog -->
    <v-dialog
      v-model="structureDialogVisible"
      persistent
      max-width="720"
      content-class="create_new_account_modal"
    >
      <div>
        <div class="structure_header">
          <h2 class="heading_new_account">Choose Structure</h2>
          <button class="close_modal" @click="closeStructureDialog"></button>
        </div>
        <div class="wrapper_content structure_content">
          <!-- Custom horizontal layout for structure options -->
          <div class="horizontal_options">
            <!-- One Artist Option -->
            <div class="structure_option" :class="{'selected': selectedStructure === 'Single Artist Release'}" @click="selectStructure('Single Artist Release')">
              <div class="option_type">
                Single Artist Release
                <span class="option_desc">One main artist and collaboration on tracks</span>
              </div>
              <div class="option_content">
                <div class="cover_preview">
                  <div class="cover_box"></div>
                  <div class="preview_artist">Artist A</div>
                </div>
              </div>
              <!-- Only show radio when structure is selected to avoid default selection -->
              <v-radio v-if="selectedStructure === 'Single Artist Release'" 
                       v-model="selectedStructure" 
                       value="Single Artist Release" 
                       color="#20C4F5" 
                       :ripple="false" 
                       class="hidden_radio"></v-radio>
            </div>

            <!-- Collaboration Option -->
            <div class="structure_option" :class="{'selected': selectedStructure === 'Multi-Artist Release'}" @click="selectStructure('Multi-Artist Release')">
              <div class="option_type">
                Multi-Artist Release
                <span class="option_desc">More than one artist collaborating on a release</span>
              </div>
              <div class="option_content">
                <div class="cover_preview">
                  <div class="cover_box"></div>
                  <div class="preview_artist">Artist A & Artist B</div>
                </div>
              </div>
              <!-- Only show radio when structure is selected to avoid default selection -->
              <v-radio v-if="selectedStructure === 'Multi-Artist Release'" 
                       v-model="selectedStructure" 
                       value="Multi-Artist Release" 
                       color="#20C4F5" 
                       :ripple="false" 
                       class="hidden_radio"></v-radio>
            </div>

            <!-- Compilation Option -->
            <div class="structure_option" :class="{'selected': selectedStructure === 'Various Artists Release'}" @click="selectStructure('Various Artists Release')">
              <div class="option_type">
                Various Artists Release
                <span class="option_desc">A collection of tracks from different artists</span>
              </div>
              <div class="option_content">
                <div class="cover_preview">
                  <div class="cover_box"></div>
                  <div class="preview_artist">Various Artists</div>
                </div>
              </div>
              <!-- Only show radio when structure is selected to avoid default selection -->
              <v-radio v-if="selectedStructure === 'Various Artists Release'" 
                       v-model="selectedStructure" 
                       value="Various Artists Release" 
                       color="#20C4F5" 
                       :ripple="false" 
                       class="hidden_radio"></v-radio>
            </div>
          </div>
        </div>
        <div class="wrapper_button center">
          <button type="button" class="tetriatary_btn" v-ripple @click="closeStructureDialog">Cancel</button>
          <button type="button" class="tetriatary_btn" v-ripple @click="confirmStructure" :disabled="!selectedStructure">Next</button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'ReleaseDialogs',
  props: {
    // Dialog visibility props
    photoLimitDialogVisible: {
      type: Boolean,
      default: false
    },
    trackLimitDialogVisible: {
      type: Boolean,
      default: false
    },
    uploadLimitDialogVisible: {
      type: Boolean,
      default: false
    },
    warningRemoveTrackDialogVisible: {
      type: Boolean,
      default: false
    },
    warningChangeArtistDialogVisible: {
      type: Boolean,
      default: false
    },
    losslessUploadedFailedDialogVisible: {
      type: Boolean,
      default: false
    },
    isOfflineVisible: {
      type: Boolean,
      default: false
    },
    structureDialogVisible: {
      type: Boolean,
      default: false
    },

    
    // Additional data props
    secondsRemaining: {
      type: Number,
      default: 15
    },
    trackDeleteProgressId: {
      type: [String, Number],
      default: null
    },
    allowRemoveTracksIndex: {
      type: Number,
      default: null
    },
    discordLink: {
      type: String,
      default: '#'
    },
    facebookLink: {
      type: String,
      default: '#'
    },
    // Initial structure value to show when dialog opens
    initialStructure: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      clearArtistsDialogVisible: false,
      artistsToClear: '',
      clearSecondaryArtistsDialogVisible: false,
      secondaryArtistsCount: 0,
      selectedStructure: null,
      artistAlreadySecondaryDialogVisible: false,
      artistToMove: '',
      artistId: null,
      labelToRemove: '',
      clearLabelDialogVisible: false
    }
  },
  computed: {
    // Watch for isTrackBeingDeleted prop changes
    isTrackBeingDeleted() {
      // TrackBeingDeleted
      return this.trackDeleteProgressId !== null && 
             this.trackDeleteProgressId !== undefined;
    }
  },
  watch: {
    // Watch for dialog visibility changes to set initial structure
    structureDialogVisible(newVal) {
      if (newVal) {
        // Reset the selection immediately when the dialog opens
        this.selectedStructure = undefined;
        
        // Use nextTick to ensure the dialog is fully rendered before setting the structure
        this.$nextTick(() => {
          this.selectedStructure = this.initialStructure || null;
          console.log('Dialog opened with initialStructure:', this.initialStructure, 'selectedStructure:', this.selectedStructure);
        });
      }
    }
  },
  methods: {
    // Clear Label Dialog
    closeClearLabelDialog() {
      this.clearLabelDialogVisible = false;
      this.$emit('cancel-clear-label');
    },
    
    cancelClearLabel() {
      console.log('Cancel clear label button clicked');
      this.clearLabelDialogVisible = false;
      this.$emit('cancel-clear-label');
    },
    
    confirmClearLabel() {
      console.log('Confirm clear label button clicked');
      this.clearLabelDialogVisible = false;
      this.$emit('confirm-clear-label');
    },
    // Photo Limit Dialog
    closePhotoLimitDialog() {
      this.$emit('update:photoLimitDialogVisible', false);
    },
    selectNewPhoto() {
      this.$emit('update:photoLimitDialogVisible', false);
      this.$emit('select-new-photo');
    },
    
    // Track Limit Dialog
    closeTrackLimitDialog() {
      this.$emit('update:trackLimitDialogVisible', false);
    },
    
    // Upload Limit Dialog
    closeUploadLimitDialog() {
      this.$emit('update:uploadLimitDialogVisible', false);
    },
    
    // Warning Remove Track Dialog
    closeWarningRemoveTrackDialog() {
      this.$emit('update:warningRemoveTrackDialogVisible', false);
    },
    confirmRemoveTrack() {
      this.$emit('confirm-remove-track', this.allowRemoveTracksIndex);
    },
    
    // Warning Change Artist Dialog
    closeWarningChangeArtistDialog() {
      this.$emit('update:warningChangeArtistDialogVisible', false);
    },
    confirmChangeArtist() {
      this.$emit('confirm-change-artist');
      this.warningChangeArtistDialogVisible = false;
    },

    // Artist Already Secondary Dialog
    closeArtistAlreadySecondaryDialog() {
      this.artistAlreadySecondaryDialogVisible = false;
      this.$emit('cancel-move-artist');
    },

    getArtistProfileUrl() {
      return `/profile/${this.artistId}`;
    },

    confirmMoveArtistToPrimary() {
      this.artistAlreadySecondaryDialogVisible = false;
      this.$emit('confirm-move-artist');
    },

    // Lossless Upload Failed Dialog
    closeLosslessUploadedFailedDialog() {
      this.$emit('update:losslessUploadedFailedDialogVisible', false);
    },
    
    // Offline Connection Dialog
    retryConnection() {
      this.$emit('retry-connection');
    },

    // Structure Dialog
    closeStructureDialog() {
      this.selectedStructure = null;
      this.$emit('update:structureDialogVisible', false);
    },
    selectStructure(structure) {
      console.log('Structure selected:', structure);
      this.selectedStructure = structure;
    },
    confirmStructure() {
      this.$emit('structure-selected', this.selectedStructure);
      this.selectedStructure = null;
      this.$emit('update:structureDialogVisible', false);
    }
  }
}
</script>

<style scoped>
.account-name-link {
  color: #20C4F5;
  text-decoration: none;
  font-weight: 500;
}

.account-name-link:hover {
  text-decoration: underline;
}
.preview_box {
  margin-top: 12px;
  text-align: center;
}

.mock_cover {
  width: 60px;
  height: 60px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  margin: 0 auto 8px;
}

.artist_display {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

/* Adjust spacing for radio group items */
.v-radio {
  margin-bottom: 24px;
}

.checkbox_label {
  padding-bottom: 8px;
}

/* Structure dialog styles */
.structure_content {
  padding: 4px 16px 12px;
  position: relative;
}

.structure_description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 24px;
  padding: 0 24px;
  text-align: center;
}

.structure_options {
  padding: 0;
  display: flex;
  flex-direction: row !important;
  gap: 12px;
  justify-content: center;
}

/* Custom horizontal layout */
.horizontal_options {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 20px;
  width: 100%;
  padding: 10px 0;
}

.structure_option {
  background: rgba(24, 32, 52, 0.5);
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  /* padding: 0 16px; */
  margin: 0;
  flex: 1;
  max-width: 220px;
  width: 220px;
}

/* Remove margin override as it's no longer needed */

.structure_option:hover {
  background: rgba(24, 32, 52, 0.8);
  border-color: rgba(32, 196, 245, 0.3);
  transform: translateY(-1px);
}

.structure_option.selected {
  background: rgba(32, 196, 245, 0.1);
  border-color: rgba(32, 196, 245, 0.4);
}

.option_type {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  padding: 16px 8px 12px;
  font-weight: 500;
  transition: color 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
}

.structure_option.selected .option_type {
  color: #20C4F5;
}

.option_desc {
  display: block;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  font-weight: normal;
  text-align: center;
  padding: 0 4px;
}

.structure_option.selected .option_desc {
  color: rgba(32, 196, 245, 0.8);
}

.hidden_radio {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  margin: 0;
  padding: 0;
}

.hidden_radio :deep(.v-input__slot) {
  margin: 0;
  padding: 0;
  min-height: 0;
}

.option_content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px 12px;
  width: 100%;
  flex: 1;
  position: relative;
  z-index: 1;
}

.cover_preview {
  text-align: center;
  flex-shrink: 0;
}

.cover_box {
  width: 90px;
  height: 90px;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  margin: 0 auto 10px;
  background: rgba(255, 255, 255, 0.03);
  transition: all 0.2s ease;
}

.structure_option.selected .cover_box {
  border-color: rgba(32, 196, 245, 0.4);
  background: rgba(32, 196, 245, 0.05);
}

.preview_artist {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-top: 10px;
  font-weight: 500;
}

/* Ensure dialog scrolls if needed */
:deep(.create_new_account_modal) {
  max-height: 90vh;
  overflow-y: auto;
  
}

.heading {
  padding-bottom: 8px;
}

/* Custom header for structure dialog with less spacing */
.structure_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* padding: 10px 20px; */
  position: relative;
  margin-bottom: 10px;
}

.structure_header .heading_new_account {
  padding-bottom: 10px;
  margin-bottom: 0;
  text-align: center;
  flex-grow: 1;
  color: #fff;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
}

/* Full-width border under the header */
.structure_header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: rgba(95, 105, 134, 0.3);
}

/* Custom styles for artist names in dialogs */
.warning-message .artist-name {
  color: #20C4F5;
  font-weight: 600;
}

.warning-message span:not(.artist-name):not(.account-name-link) {
  color: #ffffff;
  font-weight: normal;
}
</style> 